import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    LayoutDashboard, Package, FileText, ChefHat, MapPin, HelpCircle, Star,
    Mail, Send, Settings, Megaphone, ClipboardList, LogOut, Loader2, AlertCircle,
    Trash2, Check, X, Pencil, Plus, Upload, Eye, EyeOff,
} from "lucide-react";
import { AdminAuthProvider, useAdminAuth } from "./AuthContext";
import { adminApi } from "./api";

/* ---------- Layout ---------- */

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/blog", label: "Blog", icon: FileText },
    { to: "/admin/recipes", label: "Recipes", icon: ChefHat },
    { to: "/admin/attractions", label: "Attractions", icon: MapPin },
    { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
    { to: "/admin/reviews", label: "Reviews", icon: Star },
    { to: "/admin/contacts", label: "Contacts", icon: Mail },
    { to: "/admin/newsletter", label: "Newsletter", icon: Send },
    { to: "/admin/banners", label: "Banners", icon: Megaphone },
    { to: "/admin/site-settings", label: "Site Settings", icon: Settings },
    { to: "/admin/audit-log", label: "Audit Log", icon: ClipboardList },
];

const AdminShell = ({ children }) => {
    const { user, signOut } = useAdminAuth();
    const nav = useNavigate();
    return (
        <div style={{ background: "#0A0A0A", minHeight: "100vh", color: "var(--ls-text)" }}>
            <Helmet>
                <title>Admin · Liquor Studio</title>
                <meta name="robots" content="noindex,nofollow,noarchive" />
            </Helmet>
            <div style={{ display: "flex", minHeight: "100vh" }}>
                <aside data-testid="adm-sidebar" style={{ width: 240, background: "#121212", borderRight: "1px solid var(--ls-border)", padding: 20, position: "sticky", top: 0, height: "100vh", overflowY: "auto" }}>
                    <div style={{ fontFamily: "Bebas Neue, sans-serif", color: "var(--ls-gold)", letterSpacing: "0.1em", fontSize: 22 }}>LIQUOR <span style={{ color: "var(--ls-red)" }}>STUDIO</span></div>
                    <div style={{ color: "var(--ls-text-muted)", fontSize: 11, letterSpacing: "0.2em", marginTop: 2, textTransform: "uppercase" }}>Admin</div>
                    <nav style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 4 }}>
                        {NAV.map(({ to, label, icon: Icon, end }) => (
                            <NavLink key={to} to={to} end={end} data-testid={`adm-nav-${label.replace(/\s+/g, "-").toLowerCase()}`}
                                style={({ isActive }) => ({
                                    display: "flex", alignItems: "center", gap: 10,
                                    padding: "9px 12px", borderRadius: 6, fontSize: 13,
                                    textDecoration: "none",
                                    color: isActive ? "var(--ls-gold)" : "var(--ls-text-secondary)",
                                    background: isActive ? "rgba(245,176,39,0.10)" : "transparent",
                                    transition: "all .2s ease",
                                })}>
                                <Icon size={15} />
                                {label}
                            </NavLink>
                        ))}
                    </nav>
                </aside>
                <main style={{ flex: 1, padding: "28px 36px", maxWidth: "calc(100vw - 240px)" }}>
                    <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
                        <div style={{ color: "var(--ls-text-muted)", fontSize: 12 }}>Signed in as <strong style={{ color: "var(--ls-text)" }}>{user?.email}</strong></div>
                        <button data-testid="adm-logout" onClick={async () => { await signOut(); nav("/admin/login"); }}
                            className="btn-ghost" style={{ fontSize: 12, padding: "8px 14px" }}>
                            <LogOut size={14} /> Sign out
                        </button>
                    </header>
                    {children}
                </main>
            </div>
        </div>
    );
};

const ProtectedAdmin = ({ children }) => {
    const { user } = useAdminAuth();
    if (user === undefined) return <div style={{ background: "#0A0A0A", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ls-text-secondary)" }}><Loader2 className="animate-spin" /></div>;
    if (!user) return <Navigate to="/admin/login" replace />;
    return <AdminShell>{children}</AdminShell>;
};

/* ---------- Login ---------- */

const AdminLogin = () => {
    const { user, signIn } = useAdminAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => { if (user) nav("/admin"); }, [user, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try { await signIn(email.trim(), password); nav("/admin"); }
        catch (ex) { setErr(ex.message || "Login failed"); }
        finally { setBusy(false); }
    };

    return (
        <div style={{ background: "#0A0A0A", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <Helmet><title>Admin Sign-in · Liquor Studio</title><meta name="robots" content="noindex,nofollow" /></Helmet>
            <form onSubmit={submit} data-testid="adm-login-form"
                style={{ width: "100%", maxWidth: 420, background: "#121212", border: "1px solid var(--ls-border)", borderRadius: 10, padding: 40 }}>
                <div style={{ textAlign: "center", marginBottom: 28 }}>
                    <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 28, letterSpacing: "0.1em", color: "var(--ls-gold)" }}>LIQUOR <span style={{ color: "var(--ls-red)" }}>STUDIO</span></div>
                    <div style={{ color: "var(--ls-text-muted)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", marginTop: 4 }}>Admin</div>
                </div>
                <h1 className="font-display" style={{ fontSize: 26, fontWeight: 500, color: "var(--ls-text)", marginBottom: 24 }}>Sign in</h1>
                <label style={{ display: "block", fontSize: 12, color: "var(--ls-text-secondary)", marginBottom: 6 }}>Email</label>
                <input data-testid="adm-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="ls-input" autoFocus autoComplete="username" />
                <label style={{ display: "block", fontSize: 12, color: "var(--ls-text-secondary)", marginTop: 16, marginBottom: 6 }}>Password</label>
                <div style={{ position: "relative" }}>
                    <input data-testid="adm-login-password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="ls-input" autoComplete="current-password" style={{ paddingRight: 42 }} />
                    <button type="button" onClick={() => setShowPw(!showPw)} aria-label="Toggle password visibility"
                        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "var(--ls-text-muted)", cursor: "pointer" }}>
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                </div>
                {err && <p role="alert" data-testid="adm-login-error" style={{ color: "var(--ls-red)", fontSize: 13, marginTop: 14 }}><AlertCircle size={14} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />{err}</p>}
                <button data-testid="adm-login-submit" type="submit" disabled={busy} className="btn-primary" style={{ marginTop: 24, width: "100%", padding: 14, fontSize: 14, justifyContent: "center" }}>
                    {busy ? <><Loader2 size={14} className="animate-spin" /> Signing in…</> : "Sign in"}
                </button>
                <p style={{ color: "var(--ls-text-muted)", fontSize: 11, marginTop: 18, textAlign: "center" }}>Single admin access. Sessions expire after 12 hours of inactivity.</p>
            </form>
        </div>
    );
};

/* ---------- Tiny UI primitives ---------- */

const Card = ({ children, style }) => (
    <div style={{ background: "#1A1A1A", border: "1px solid var(--ls-border)", borderRadius: 8, padding: 22, ...style }}>{children}</div>
);
const Title = ({ children, action }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1 className="font-display" style={{ fontSize: 30, fontWeight: 500, color: "var(--ls-text)" }}>{children}</h1>
        {action}
    </div>
);
const Toast = ({ msg, type = "success", onClose }) => msg ? (
    <div role="status" data-testid="adm-toast"
        style={{ position: "fixed", bottom: 20, right: 20, background: type === "error" ? "rgba(231,76,60,0.15)" : "rgba(245,176,39,0.15)", border: `1px solid ${type === "error" ? "var(--ls-red)" : "var(--ls-gold)"}`, color: "var(--ls-text)", padding: "12px 18px", borderRadius: 6, fontSize: 14, zIndex: 100 }}>
        {msg}
        <button onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--ls-text-muted)", marginLeft: 12, cursor: "pointer" }}>×</button>
    </div>
) : null;

const useToast = () => {
    const [t, setT] = useState({ msg: "", type: "success" });
    const show = (msg, type = "success") => { setT({ msg, type }); setTimeout(() => setT({ msg: "", type }), 4000); };
    return [t, show];
};

/* ---------- Dashboard ---------- */

const Dashboard = () => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        adminApi.dashboard().then(setData).catch((e) => setErr(e.message));
    }, []);
    if (err) return <Card><p style={{ color: "var(--ls-red)" }}>{err}</p></Card>;
    if (!data) return <div><Loader2 className="animate-spin" /></div>;
    const c = data.counts;
    const tiles = [
        { k: "Products", v: c.products, link: "/admin/products" },
        { k: "Blog Posts", v: c.blog_posts, link: "/admin/blog" },
        { k: "Recipes", v: c.recipes, link: "/admin/recipes" },
        { k: "Attractions", v: c.attractions, link: "/admin/attractions" },
        { k: "FAQs", v: c.faqs, link: "/admin/faqs" },
        { k: "Reviews · Pending", v: c.reviews_pending, link: "/admin/reviews", hot: c.reviews_pending > 0 },
        { k: "Contacts · New", v: c.contacts_new, link: "/admin/contacts", hot: c.contacts_new > 0 },
        { k: "Subscribers", v: c.subscribers, link: "/admin/newsletter" },
        { k: "Active Banner", v: c.active_banner, link: "/admin/banners" },
    ];
    return (
        <>
            <Title>Dashboard</Title>
            <div data-testid="adm-dashboard-tiles" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
                {tiles.map((t) => (
                    <NavLink key={t.k} to={t.link} data-testid={`adm-tile-${t.k.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        style={{ textDecoration: "none", background: "#1A1A1A", border: `1px solid ${t.hot ? "var(--ls-red)" : "var(--ls-border)"}`, borderRadius: 8, padding: 22, display: "block" }}>
                        <div style={{ color: t.hot ? "var(--ls-red)" : "var(--ls-text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>{t.k}</div>
                        <div className="font-display" style={{ fontSize: 38, fontWeight: 500, color: "var(--ls-text)", marginTop: 6 }}>{t.v}</div>
                    </NavLink>
                ))}
            </div>
            <h2 className="font-display" style={{ fontSize: 22, fontWeight: 500, marginTop: 36, color: "var(--ls-text)" }}>Recent activity</h2>
            <Card style={{ marginTop: 12 }}>
                {data.recent_activity.length === 0 ? <p style={{ color: "var(--ls-text-muted)", fontSize: 13 }}>No recent activity.</p> :
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="adm-recent-activity">
                        <thead><tr style={{ color: "var(--ls-text-muted)", textAlign: "left" }}><th style={{ padding: 8 }}>When</th><th>User</th><th>Action</th><th>Resource</th></tr></thead>
                        <tbody>
                            {data.recent_activity.map((a) => (
                                <tr key={a.id} style={{ borderTop: "1px solid var(--ls-border)" }}>
                                    <td style={{ padding: 8, color: "var(--ls-text-muted)" }}>{new Date(a.timestamp).toLocaleString()}</td>
                                    <td>{a.user_email}</td>
                                    <td>{a.action}</td>
                                    <td>{a.resource_type}{a.resource_id ? ` · ${a.resource_id.slice(0, 8)}` : ""}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>}
            </Card>
        </>
    );
};

/* ---------- Generic Resource Manager ---------- */
/* Lightweight CRUD for resources whose form is simple JSON-editable in textarea (admins
   can use the structured tabs for routine work and JSON for power edits). Each resource
   exports its column config + JSON template. */

const RES = {
    products: { name: "Products", endpoint: "products", titleField: "name", subField: "brand" },
    blog: { name: "Blog Posts", endpoint: "blog", titleField: "title", subField: "category" },
    recipes: { name: "Recipes", endpoint: "recipes", titleField: "name", subField: "glassware" },
    attractions: { name: "Attractions", endpoint: "attractions", titleField: "name", subField: "distance_from_store" },
    faqs: { name: "FAQs", endpoint: "faqs", titleField: "question", subField: "category" },
    banners: { name: "Banners", endpoint: "banners", titleField: "message", subField: "style" },
};

const ResourceList = ({ resource }) => {
    const cfg = RES[resource];
    const nav = useNavigate();
    const [items, setItems] = useState(null);
    const [err, setErr] = useState("");
    const [toast, showToast] = useToast();
    const [confirmDel, setConfirmDel] = useState(null);

    const load = () => {
        adminApi.list(cfg.endpoint, { limit: 200 }).then((d) => setItems(d.items || [])).catch((e) => setErr(e.message));
    };
    useEffect(load, [resource]); // eslint-disable-line

    const doDelete = async () => {
        try {
            await adminApi.remove(cfg.endpoint, confirmDel.id);
            setConfirmDel(null);
            showToast("Deleted");
            load();
        } catch (e) { showToast(e.message, "error"); }
    };

    return (
        <>
            <Title action={
                <button data-testid={`adm-${resource}-new`} className="btn-primary" style={{ fontSize: 13, padding: "10px 18px" }}
                    onClick={() => nav(`/admin/${resource}/new`)}><Plus size={14} /> New</button>
            }>{cfg.name}</Title>
            {err && <Card style={{ marginBottom: 16 }}><p style={{ color: "var(--ls-red)" }}>{err}</p></Card>}
            <Card style={{ padding: 0, overflow: "hidden" }}>
                {!items ? <div style={{ padding: 30 }}><Loader2 className="animate-spin" /></div> :
                    items.length === 0 ? <p style={{ padding: 30, color: "var(--ls-text-muted)" }}>No items yet.</p> :
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid={`adm-${resource}-table`}>
                            <thead><tr style={{ background: "#0F0F0F", color: "var(--ls-text-muted)", textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                                <th style={{ padding: 12 }}>Title</th><th>Detail</th><th style={{ width: 220 }}>Actions</th>
                            </tr></thead>
                            <tbody>
                                {items.map((it) => (
                                    <tr key={it.id} data-testid={`adm-${resource}-row`} style={{ borderTop: "1px solid var(--ls-border)" }}>
                                        <td style={{ padding: 12 }}>
                                            <div style={{ color: "var(--ls-text)", fontWeight: 500 }}>{String(it[cfg.titleField] || "(untitled)").slice(0, 100)}</div>
                                            <div style={{ color: "var(--ls-text-muted)", fontSize: 11 }}>{it.slug || it.id.slice(0, 8)}</div>
                                        </td>
                                        <td style={{ color: "var(--ls-text-secondary)" }}>{it[cfg.subField] || "—"}</td>
                                        <td style={{ padding: 8 }}>
                                            <button onClick={() => nav(`/admin/${resource}/${it.id}/edit`)} className="btn-ghost" style={{ fontSize: 12, padding: "6px 10px", marginRight: 6 }} data-testid={`adm-${resource}-edit`}>
                                                <Pencil size={12} /> Edit
                                            </button>
                                            <button onClick={() => setConfirmDel(it)} className="btn-ghost" style={{ fontSize: 12, padding: "6px 10px", color: "var(--ls-red)" }} data-testid={`adm-${resource}-delete`}>
                                                <Trash2 size={12} /> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                }
            </Card>
            {confirmDel && (
                <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 90 }}>
                    <Card style={{ width: 420 }}>
                        <h3 className="font-display" style={{ fontSize: 22, fontWeight: 500, color: "var(--ls-text)" }}>Delete?</h3>
                        <p style={{ color: "var(--ls-text-secondary)", fontSize: 13, marginTop: 8 }}>This action cannot be undone.</p>
                        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
                            <button className="btn-ghost" onClick={() => setConfirmDel(null)} style={{ fontSize: 13 }} data-testid="adm-delete-cancel">Cancel</button>
                            <button className="btn-primary" onClick={doDelete} style={{ fontSize: 13, background: "var(--ls-red)", borderColor: "var(--ls-red)" }} data-testid="adm-delete-confirm">Delete</button>
                        </div>
                    </Card>
                </div>
            )}
            <Toast {...toast} onClose={() => showToast("")} />
        </>
    );
};

const ResourceForm = ({ resource }) => {
    const cfg = RES[resource];
    const { id } = useParams();
    const isEdit = !!id;
    const nav = useNavigate();
    const [doc, setDoc] = useState("");
    const [loading, setLoading] = useState(isEdit);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (isEdit) {
            adminApi.getOne(cfg.endpoint, id).then((d) => {
                delete d._id;
                setDoc(JSON.stringify(d, null, 2));
                setLoading(false);
            }).catch((e) => { setErr(e.message); setLoading(false); });
        } else {
            // template by resource
            const templates = {
                products: { slug: "", name: "", category: "bourbon", brand: "", description: "", short_description: "", price_display: "", image_url: "", tags: [], featured: false, in_stock: true, shop_url: "https://shop.liquor.studio/" },
                blog: { slug: "", title: "", excerpt: "", content: "", cover_image_url: "", author: "Liquor Studio Team", category: "Guides", tags: [], published: true, reading_minutes: 5 },
                recipes: { slug: "", name: "", description: "", cover_image_url: "", difficulty: "Easy", prep_minutes: 5, servings: 1, glassware: "Rocks", ingredients: [{ name: "", amount: "", unit: "" }], instructions: [""], tags: [] },
                attractions: { slug: "", name: "", short_description: "", content: "", cover_image_url: "", distance_from_store: "", recommended_products: [], tags: [] },
                faqs: { question: "", answer: "", category: "Store Info", order: 0, published: true },
                banners: { message: "", link_url: "", link_label: "", active: true, style: "info" },
            };
            setDoc(JSON.stringify(templates[resource], null, 2));
        }
    }, [resource, id, isEdit, cfg.endpoint]);

    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const res = await adminApi.upload(f);
            // append url into JSON as image_url / cover_image_url
            try {
                const obj = JSON.parse(doc);
                const key = "image_url" in obj ? "image_url" : ("cover_image_url" in obj ? "cover_image_url" : null);
                if (key) {
                    obj[key] = `${process.env.REACT_APP_BACKEND_URL}${res.url}`;
                    setDoc(JSON.stringify(obj, null, 2));
                }
            } catch { /* ignore */ }
            alert(`Uploaded: ${res.url}`);
        } catch (ex) { alert(ex.message); }
    };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        let payload;
        try { payload = JSON.parse(doc); }
        catch (ex) { setErr("Invalid JSON: " + ex.message); setBusy(false); return; }
        try {
            if (isEdit) await adminApi.update(cfg.endpoint, id, payload);
            else await adminApi.create(cfg.endpoint, payload);
            nav(`/admin/${resource}`);
        } catch (ex) { setErr(ex.message); }
        finally { setBusy(false); }
    };

    return (
        <>
            <Title>{isEdit ? "Edit" : "New"} · {cfg.name}</Title>
            {loading ? <Loader2 className="animate-spin" /> :
                <form onSubmit={save} data-testid={`adm-${resource}-form`}>
                    <Card>
                        <label style={{ display: "block", fontSize: 12, color: "var(--ls-text-secondary)", marginBottom: 8 }}>JSON payload</label>
                        <textarea value={doc} onChange={(e) => setDoc(e.target.value)} rows={28}
                            className="ls-input" style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12, lineHeight: 1.6, minHeight: 400 }}
                            data-testid={`adm-${resource}-json`} />
                        <div style={{ display: "flex", gap: 12, marginTop: 14, alignItems: "center", flexWrap: "wrap" }}>
                            <label className="btn-ghost" style={{ fontSize: 12, padding: "8px 14px", cursor: "pointer", margin: 0 }}>
                                <Upload size={12} /> Upload image
                                <input type="file" accept="image/*" style={{ display: "none" }} onChange={upload} data-testid={`adm-${resource}-upload`} />
                            </label>
                            <span style={{ color: "var(--ls-text-muted)", fontSize: 11 }}>PNG/JPG/WebP, ≤ 5 MB — sets image_url / cover_image_url on success.</span>
                        </div>
                        {err && <p role="alert" style={{ color: "var(--ls-red)", fontSize: 13, marginTop: 14 }}>{err}</p>}
                    </Card>
                    <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                        <button type="submit" disabled={busy} className="btn-primary" style={{ fontSize: 13 }} data-testid={`adm-${resource}-save`}>
                            {busy ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : "Save"}
                        </button>
                        <button type="button" onClick={() => nav(`/admin/${resource}`)} className="btn-ghost" style={{ fontSize: 13 }}>Cancel</button>
                    </div>
                </form>}
        </>
    );
};

/* ---------- Reviews moderation ---------- */

const Reviews = () => {
    const [tab, setTab] = useState("pending");
    const [items, setItems] = useState(null);
    const [toast, showToast] = useToast();
    const load = () => adminApi.list("reviews", { status: tab, limit: 200 }).then((d) => setItems(d.items || []));
    useEffect(() => { setItems(null); load(); }, [tab]); // eslint-disable-line
    const act = async (id, kind) => {
        try {
            if (kind === "approve") await adminApi.approveReview(id);
            if (kind === "reject") await adminApi.rejectReview(id);
            if (kind === "delete") await adminApi.remove("reviews", id);
            showToast(`Review ${kind}d`);
            load();
        } catch (e) { showToast(e.message, "error"); }
    };
    return (
        <>
            <Title>Reviews moderation</Title>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }} role="tablist">
                {["pending", "approved", "rejected", "all"].map((s) => (
                    <button key={s} role="tab" aria-selected={tab === s} onClick={() => setTab(s)}
                        data-testid={`adm-rev-tab-${s}`}
                        style={{ padding: "8px 16px", borderRadius: 999, border: `1px solid ${tab === s ? "var(--ls-gold)" : "var(--ls-border-strong)"}`, background: tab === s ? "rgba(245,176,39,0.10)" : "transparent", color: tab === s ? "var(--ls-gold)" : "var(--ls-text-secondary)", fontSize: 12, cursor: "pointer", textTransform: "capitalize" }}>
                        {s}
                    </button>
                ))}
            </div>
            <Card style={{ padding: 0, overflow: "hidden" }}>
                {!items ? <div style={{ padding: 30 }}><Loader2 className="animate-spin" /></div> :
                    items.length === 0 ? <p style={{ padding: 30, color: "var(--ls-text-muted)" }}>None.</p> :
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="adm-reviews-table">
                            <tbody>
                                {items.map((r) => (
                                    <tr key={r.id} style={{ borderTop: "1px solid var(--ls-border)" }}>
                                        <td style={{ padding: 12 }}>
                                            <div style={{ color: "var(--ls-gold)" }}>{"★".repeat(r.rating)}</div>
                                            <div style={{ color: "var(--ls-text)", fontWeight: 500 }}>{r.author_name} <span style={{ color: "var(--ls-text-muted)", fontWeight: 400 }}>· {r.source}</span></div>
                                            <div style={{ color: "var(--ls-text-secondary)", fontSize: 12, marginTop: 4, maxWidth: 540 }}>"{r.body.slice(0, 220)}{r.body.length > 220 ? "…" : ""}"</div>
                                        </td>
                                        <td style={{ padding: 8, width: 280, textAlign: "right" }}>
                                            {r.status !== "approved" && <button onClick={() => act(r.id, "approve")} className="btn-ghost" style={{ fontSize: 12, padding: "6px 10px", marginRight: 6, color: "var(--ls-gold)" }} data-testid={`adm-rev-approve-${r.id}`}><Check size={12} /> Approve</button>}
                                            {r.status !== "rejected" && <button onClick={() => act(r.id, "reject")} className="btn-ghost" style={{ fontSize: 12, padding: "6px 10px", marginRight: 6 }} data-testid={`adm-rev-reject-${r.id}`}><X size={12} /> Reject</button>}
                                            <button onClick={() => act(r.id, "delete")} className="btn-ghost" style={{ fontSize: 12, padding: "6px 10px", color: "var(--ls-red)" }} data-testid={`adm-rev-delete-${r.id}`}><Trash2 size={12} /></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>}
            </Card>
            <Toast {...toast} onClose={() => showToast("")} />
        </>
    );
};

/* ---------- Contacts ---------- */

const Contacts = () => {
    const [items, setItems] = useState(null);
    const [toast, showToast] = useToast();
    const load = () => adminApi.list("contact-messages", { limit: 200 }).then((d) => setItems(d.items || []));
    useEffect(load, []);
    const setStatus = async (id, status) => {
        try { await adminApi.contactStatus(id, status); showToast("Status updated"); load(); }
        catch (e) { showToast(e.message, "error"); }
    };
    return (
        <>
            <Title>Contact Inquiries</Title>
            <Card style={{ padding: 0 }}>
                {!items ? <div style={{ padding: 30 }}><Loader2 className="animate-spin" /></div> :
                    items.length === 0 ? <p style={{ padding: 30, color: "var(--ls-text-muted)" }}>None.</p> :
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="adm-contacts-table">
                            <tbody>
                                {items.map((c) => (
                                    <tr key={c.id} style={{ borderTop: "1px solid var(--ls-border)" }}>
                                        <td style={{ padding: 12, verticalAlign: "top" }}>
                                            <div style={{ color: "var(--ls-text)", fontWeight: 500 }}>{c.name}</div>
                                            <div style={{ color: "var(--ls-text-muted)", fontSize: 11 }}>{c.email} · {c.subject}</div>
                                            <p style={{ color: "var(--ls-text-secondary)", fontSize: 12, marginTop: 6, maxWidth: 600 }}>{c.message}</p>
                                        </td>
                                        <td style={{ padding: 8, width: 260, textAlign: "right" }}>
                                            <select value={c.status} onChange={(e) => setStatus(c.id, e.target.value)} className="ls-input" style={{ fontSize: 12, width: 130, padding: "6px 10px" }} data-testid={`adm-contact-status-${c.id}`}>
                                                <option value="new">New</option>
                                                <option value="read">Read</option>
                                                <option value="archived">Archived</option>
                                            </select>
                                            <a href={`mailto:${c.email}?subject=Re: ${encodeURIComponent(c.subject)}`} className="btn-ghost" style={{ fontSize: 12, padding: "6px 10px", marginLeft: 6 }}>Reply</a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>}
            </Card>
            <Toast {...toast} onClose={() => showToast("")} />
        </>
    );
};

/* ---------- Newsletter ---------- */

const Newsletter = () => {
    const [items, setItems] = useState(null);
    const { token } = useAdminAuth();
    useEffect(() => { adminApi.list("newsletter", { limit: 500 }).then((d) => setItems(d.items || [])); }, []);
    const download = async () => {
        const res = await fetch(adminApi.newsletterExportUrl(), { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "newsletter.csv"; a.click();
        URL.revokeObjectURL(url);
    };
    return (
        <>
            <Title action={<button onClick={download} className="btn-primary" style={{ fontSize: 13 }} data-testid="adm-newsletter-export">Export CSV</button>}>Newsletter Subscribers</Title>
            <Card style={{ padding: 0 }}>
                {!items ? <div style={{ padding: 30 }}><Loader2 className="animate-spin" /></div> :
                    items.length === 0 ? <p style={{ padding: 30, color: "var(--ls-text-muted)" }}>None yet.</p> :
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="adm-newsletter-table">
                            <thead><tr style={{ background: "#0F0F0F", color: "var(--ls-text-muted)", textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}><th style={{ padding: 12 }}>Email</th><th>Source</th><th>Status</th><th>Subscribed</th></tr></thead>
                            <tbody>
                                {items.map((s) => (
                                    <tr key={s.id} style={{ borderTop: "1px solid var(--ls-border)" }}>
                                        <td style={{ padding: 12, color: "var(--ls-text)" }}>{s.email}</td>
                                        <td style={{ color: "var(--ls-text-secondary)" }}>{s.source}</td>
                                        <td><span style={{ color: s.status === "subscribed" ? "var(--ls-gold)" : "var(--ls-text-muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>{s.status}</span></td>
                                        <td style={{ color: "var(--ls-text-muted)", fontSize: 12 }}>{new Date(s.consent_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>}
            </Card>
        </>
    );
};

/* ---------- Site settings ---------- */

const SiteSettings = () => {
    const [doc, setDoc] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [toast, showToast] = useToast();
    useEffect(() => {
        adminApi.getSettings().then((d) => { delete d._id; setDoc(JSON.stringify(d, null, 2)); setLoading(false); });
    }, []);
    const save = async () => {
        setBusy(true);
        try {
            const payload = JSON.parse(doc);
            // strip read-only fields server doesn't accept
            delete payload.id; delete payload.updated_at;
            await adminApi.updateSettings(payload);
            showToast("Settings saved — public site now reflects changes.");
        } catch (e) { showToast(e.message, "error"); }
        finally { setBusy(false); }
    };
    return (
        <>
            <Title action={<button onClick={save} disabled={busy} className="btn-primary" style={{ fontSize: 13 }} data-testid="adm-settings-save">{busy ? "Saving…" : "Save"}</button>}>Site Settings</Title>
            <Card>
                {loading ? <Loader2 className="animate-spin" /> :
                    <textarea value={doc} onChange={(e) => setDoc(e.target.value)} rows={32} className="ls-input" data-testid="adm-settings-json"
                        style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12, lineHeight: 1.6 }} />}
            </Card>
            <Toast {...toast} onClose={() => showToast("")} />
        </>
    );
};

/* ---------- Audit log ---------- */

const AuditLog = () => {
    const [items, setItems] = useState(null);
    useEffect(() => { adminApi.auditLog({ limit: 200 }).then((d) => setItems(d.items || [])); }, []);
    return (
        <>
            <Title>Audit Log</Title>
            <Card style={{ padding: 0 }}>
                {!items ? <div style={{ padding: 30 }}><Loader2 className="animate-spin" /></div> :
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }} data-testid="adm-audit-table">
                        <thead><tr style={{ background: "#0F0F0F", color: "var(--ls-text-muted)", textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                            <th style={{ padding: 12 }}>When</th><th>User</th><th>Action</th><th>Resource</th><th>Notes</th>
                        </tr></thead>
                        <tbody>
                            {items.map((a) => (
                                <tr key={a.id} style={{ borderTop: "1px solid var(--ls-border)" }}>
                                    <td style={{ padding: 10, color: "var(--ls-text-muted)" }}>{new Date(a.timestamp).toLocaleString()}</td>
                                    <td>{a.user_email}</td>
                                    <td style={{ color: "var(--ls-gold)" }}>{a.action}</td>
                                    <td>{a.resource_type}{a.resource_id ? ` · ${a.resource_id.slice(0, 8)}` : ""}</td>
                                    <td style={{ color: "var(--ls-text-secondary)" }}>{a.diff_summary}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>}
            </Card>
        </>
    );
};

/* ---------- Root admin router ---------- */

const AdminInner = () => (
    <Routes>
        <Route path="login" element={<AdminLogin />} />
        <Route index element={<ProtectedAdmin><Dashboard /></ProtectedAdmin>} />
        {["products", "blog", "recipes", "attractions", "faqs", "banners"].map((r) => (
            <React.Fragment key={r}>
                <Route path={r} element={<ProtectedAdmin><ResourceList resource={r} /></ProtectedAdmin>} />
                <Route path={`${r}/new`} element={<ProtectedAdmin><ResourceForm resource={r} /></ProtectedAdmin>} />
                <Route path={`${r}/:id/edit`} element={<ProtectedAdmin><ResourceForm resource={r} /></ProtectedAdmin>} />
            </React.Fragment>
        ))}
        <Route path="reviews" element={<ProtectedAdmin><Reviews /></ProtectedAdmin>} />
        <Route path="contacts" element={<ProtectedAdmin><Contacts /></ProtectedAdmin>} />
        <Route path="newsletter" element={<ProtectedAdmin><Newsletter /></ProtectedAdmin>} />
        <Route path="site-settings" element={<ProtectedAdmin><SiteSettings /></ProtectedAdmin>} />
        <Route path="audit-log" element={<ProtectedAdmin><AuditLog /></ProtectedAdmin>} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
);

export const AdminApp = () => (
    <AdminAuthProvider>
        <Helmet prioritizeSeoTags>
            <meta name="robots" content="noindex,nofollow,noarchive,nosnippet" />
            <meta name="googlebot" content="noindex,nofollow" />
            <title>Admin · Liquor Studio</title>
        </Helmet>
        <AdminInner />
    </AdminAuthProvider>
);

export default AdminApp;
